import { useState } from 'react';
import { Alert, Button, SpaceBetween, Container, FormField, Input } from '@amzn/awsui-components-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import CameraModal from '../../components/cameraModal';
import decodeImage from '../../components/camera/imageDecoder';
import DrishtiPageHeader from '../../components/drishtiPageHeader';
import {
    DRISHTI_INSTALL_OUTCOME,
    startDrishtiInstall,
    toStartDrishtiInstallResponse,
    type StartDrishtiInstallResponse,
} from '../../services/drishtiHandler';
import { EventName, EventType, logDrishtiMetricEvent } from '../../services/eventService';
import { useAppSelector } from '../../app/hooks';
import { installResumed, updateDrishtiDeviceSerialDraft } from '../../slice/drishtiInstallConfigSlice';
import {
    DRISHTI_LIFECYCLE_STATE,
    RESUME_PATH,
    toDrishtiLifecycleState,
    type DrishtiLifecycleState,
} from '../../constants/drishtiLifecycle';
import {
    formatStation,
    isCompleteStationConfig,
    isMentatSerial,
    isPartialMentatSerial,
    MENTAT_SERIAL_LENGTH,
    type StationKey,
} from '../../constants/stationConfig';

type RegisterAction =
    | { readonly kind: 'resume'; readonly lifecycleState: DrishtiLifecycleState }
    | { readonly kind: 'blocked'; readonly message: string }
    | { readonly kind: 'unroutable' };

const registerAction = (response: StartDrishtiInstallResponse, station: StationKey): RegisterAction => {
    const { assignedSite, assignedStationNumber, blockingDeviceId, deviceId } = response;
    switch (response.outcome) {
        case DRISHTI_INSTALL_OUTCOME.BLOCKED_DEVICE_ACTIVE_ELSEWHERE: {
            const where = assignedSite && assignedStationNumber ? `${assignedSite}-${assignedStationNumber}` : null;
            return {
                kind: 'blocked',
                message: `Device ${deviceId} is already active at ${
                    where ?? 'another workstation'
                }. Scan a different device.`,
            };
        }
        case DRISHTI_INSTALL_OUTCOME.BLOCKED_STATION_OCCUPIED:
            return {
                kind: 'blocked',
                message: `Workstation ${formatStation(station)} is already assigned to ${
                    blockingDeviceId ? `active device ${blockingDeviceId}` : 'another active device'
                }. Choose a different workstation.`,
            };
        case DRISHTI_INSTALL_OUTCOME.STARTED:
            return {
                kind: 'resume',
                lifecycleState:
                    toDrishtiLifecycleState(response.lifecycleState) ?? DRISHTI_LIFECYCLE_STATE.INSTALL_STARTED,
            };
        case DRISHTI_INSTALL_OUTCOME.RESUMED: {
            const lifecycleState = toDrishtiLifecycleState(response.lifecycleState);
            return lifecycleState ? { kind: 'resume', lifecycleState } : { kind: 'unroutable' };
        }
    }
};

const DrishtiConfig = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const config = useAppSelector((state) => state.drishtiInstallConfig);

    // TODO: seed with MENTAT_SERIAL_PREFIX again when the prefix check comes back.
    const deviceSerial = config.deviceSerialDraft ?? '';
    const [isScanning, setIsScanning] = useState(false);
    const [isScanLoading, setIsScanLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deviceScanError, setDeviceScanError] = useState('');
    const [confirmError, setConfirmError] = useState('');
    const [refusal, setRefusal] = useState('');

    if (!config.site) {
        return <Navigate to="/drishti/site" replace={true} />;
    }
    if (!config.stationNumber || !isCompleteStationConfig(config.stationConfig)) {
        return <Navigate to="/drishti/station" replace={true} />;
    }

    const station = { site: config.site, stationNumber: config.stationNumber };
    const isValidDeviceSerial = isMentatSerial(deviceSerial);
    const deviceSerialError =
        deviceSerial && !isValidDeviceSerial && !isPartialMentatSerial(deviceSerial)
            ? `Must be ${MENTAT_SERIAL_LENGTH} characters.`
            : '';

    const logEvent = (eventName: EventName, eventType: EventType, miscEventData?: {}) => {
        logDrishtiMetricEvent({ eventName, eventType, deviceId: deviceSerial || undefined, miscEventData });
    };

    const onDeviceChange = (value: string) => {
        dispatch(updateDrishtiDeviceSerialDraft(value.trim()));
        setDeviceScanError('');
        setConfirmError('');
        setRefusal('');
    };

    const onScanCapture = (img: unknown) => {
        setIsScanLoading(true);
        decodeImage(img, (text?: string, error?: unknown) => {
            setIsScanLoading(false);
            if (!text) {
                logEvent(EventName.DRISHTI_SERIAL_DECODE_FROM_CAMERA, EventType.FAILURE, {
                    error: error instanceof Error ? error.message : error,
                });
                setDeviceScanError('Could not read the barcode. Retake the photo or enter the value manually.');
                return;
            }
            onDeviceChange(text);
        });
    };

    const onConfirm = () => {
        if (!isValidDeviceSerial) {
            return;
        }
        setLoading(true);
        setConfirmError('');
        setRefusal('');
        startDrishtiInstall({ ...station, deviceSerial }, (success, result) => {
            setLoading(false);
            const response = success ? toStartDrishtiInstallResponse(result) : null;
            if (!response) {
                setConfirmError('Could not start the install. Please try again.');
                logEvent(EventName.DRISHTI_INSTALL_STARTED, EventType.FAILURE, { ...station, error: result });
                return;
            }

            const outcomeData = {
                ...station,
                outcome: response.outcome,
                lifecycleState: response.lifecycleState,
                blockingDeviceId: response.blockingDeviceId,
                assignedSite: response.assignedSite,
                assignedStationNumber: response.assignedStationNumber,
            };
            const action = registerAction(response, station);

            if (action.kind === 'blocked') {
                setRefusal(action.message);
                logEvent(EventName.DRISHTI_INSTALL_STARTED, EventType.FAILED_VALIDATION, outcomeData);
                return;
            }
            if (action.kind === 'unroutable') {
                setConfirmError(
                    `Device ${response.deviceId} is in a state this app does not recognize. Install a different device.`,
                );
                logEvent(EventName.DRISHTI_INSTALL_STARTED, EventType.FAILED_VALIDATION, outcomeData);
                return;
            }

            dispatch(installResumed({ deviceSerial, lifecycleState: action.lifecycleState }));
            const destination = RESUME_PATH[action.lifecycleState];
            // resumedState is the state that picked the screen; outcomeData.lifecycleState is the raw response value.
            logEvent(EventName.DRISHTI_INSTALL_STARTED, EventType.SUCCESS, {
                ...outcomeData,
                resumedState: action.lifecycleState,
                destination,
            });
            navigate(destination);
        });
    };

    return (
        <Container>
            <DrishtiPageHeader
                title={`Scan the device at ${formatStation(station)}`}
                description="Scan the barcode on the device you are installing, then start the install."
            />
            <SpaceBetween size="l">
                <FormField
                    label="Device serial"
                    description={`Mentat serials are ${MENTAT_SERIAL_LENGTH} characters.`}
                    errorText={deviceSerialError}
                >
                    <SpaceBetween direction="horizontal" size="xs">
                        <Input
                            value={deviceSerial}
                            disabled={loading}
                            onChange={(e) => onDeviceChange(e.detail.value)}
                        />
                        <Button
                            iconName="video-on"
                            loading={isScanLoading}
                            disabled={loading}
                            onClick={() => setIsScanning(true)}
                        >
                            Scan device
                        </Button>
                    </SpaceBetween>
                </FormField>

                {deviceScanError ? <Alert type="error">{deviceScanError}</Alert> : null}

                {confirmError ? <Alert type="error">{confirmError}</Alert> : null}

                {refusal ? (
                    <Alert type="warning" header="This device cannot be installed here">
                        {refusal}
                    </Alert>
                ) : null}

                <Button variant="primary" onClick={onConfirm} loading={loading} disabled={!isValidDeviceSerial}>
                    Confirm and start install
                </Button>
            </SpaceBetween>

            <CameraModal
                header="Take a picture of the device barcode"
                visible={isScanning}
                setVisible={() => setIsScanning(false)}
                setUserImageCallback={onScanCapture}
                isValidTextOutput={() => true}
                setTextOutput={() => undefined}
            />
        </Container>
    );
};

export default DrishtiConfig;
